import React, { Component } from "react";
import { Row, Card, Col, Divider, Menu, Dropdown, Button, Select, Alert } from "antd";
import Helmet from "react-helmet";
import TopTable from "./top-table.js";
import MultiSelect from "../common/multi-select";
import { getDistinctValues } from "../../services/api-prosecution";
import { omitBy, isEmpty } from "lodash";
import { keepOnlyProsecutionQueryStrings } from "../../helpers/url";
import TopTableLpix from "./top-table-lpix.js";
import { DownOutlined } from "@ant-design/icons";

const styles = {
  body: {
    width: "100%",
    padding: 30
  },
  card: {
    marginBottom: 16
  },
  table: {
    textAlign: "center"
  }
};

const defaultFilter = { application_type: "utility", filing_date: "2000-01-01--" };

const propsToStates = q => ({
  examiner_art_unit: q.art_unit
});

class Prosecution extends Component {
  state = {
    ...propsToStates(keepOnlyProsecutionQueryStrings(this.props.location.query)),
    currentLawFirmView: "LPIX",
    topSize: 20
  };

  render() {
    const helmet = {
      title: "Prosecution Analytics",
      meta: [
        {
          name: "keywords",
          content: `USPTO,patent prosecution,patent examination,patent examiner,law firm,allowance rate,PatEx,PEDS,ODP,Patent Examination Data System,Open Data Portal`
        },
        {
          name: "description",
          content: `Unified’s Patent Prosecution Analytics tool provides data about the top patent examiners and law firms. With information like patent application allowance rates by art unit, users can develop an informed patent prosecution strategy.`
        }
      ]
    };
    const filter = this._getFilter();
    const LawFirmViewSelector = () => (
      <Menu onClick={({ key }) => this.setState({ currentLawFirmView: key })}>
        <Menu.Item key="LPIX">LPIX</Menu.Item>
        <Menu.Item key="Allowance Rate">Allowance Rate</Menu.Item>
      </Menu>
    );
    const { topSize, currentLawFirmView } = this.state;
    return (
      <div style={styles.body} key={this.props.location.query.name}>
        <Helmet {...helmet} />
        <Row gutter={16}>
          <Col span={24} xl={12}>
            <Card title="Pick an Examiner" style={{ marginBottom: 0 }}>
              <MultiSelect
                mode="default"
                onChange={val =>
                  this.props.router.push({
                    pathname: `${this.props.location.pathname}/examiner/${val}`
                  })
                }
                placeholder="Please type examiner name to search, e.g. Young, Lee W"
                optionApiCall={val =>
                  getDistinctValues("examiner_name", {
                    examiner_name: val || ""
                  })
                }
                style={{ width: "100%" }}
              />
            </Card>
          </Col>
          <Col span={24} xl={12}>
            <Card title="Pick a Law Firm" style={{ marginBottom: 0 }}>
              <MultiSelect
                mode="default"
                onChange={val =>
                  this.props.router.push({
                    pathname: `${this.props.location.pathname}/lawfirm/${val}`
                  })
                }
                placeholder="Please type law firm name to search, e.g. Ford Global Technologies, LLC"
                optionApiCall={val =>
                  getDistinctValues("law_firm", {
                    law_firm: val || ""
                  })
                }
                style={{ width: "100%" }}
              />
            </Card>
          </Col>
        </Row>
        <Divider />
        <Alert message="Analytics below only cover utility applications filed after 2000-01-01." type="info" style={{ marginBottom: 16 }} />
        <Row>
          <Col span={24} xl={24}>
            <MultiSelect
              value={this.state.examiner_art_unit}
              onChange={val =>
                this.setState(
                  {
                    examiner_art_unit: val
                  },
                  () =>
                    this.props.router.push({
                      pathname: this.props.location.pathname,
                      query: { art_unit: val }
                    })
                )
              }
              placeholder="Drill down by Art Unit, please select an art unit, e.g. 2872"
              optionApiCall={val =>
                getDistinctValues("examiner_art_unit", {
                  ...defaultFilter,
                  examiner_art_unit: val || ""
                }).then(json => json.filter(au => !au.endsWith("00")))
              }
              style={{ width: "calc(100% - 135px)", marginBottom: 16 }}
            />
            <Select value={topSize} placeholder="Display Size" onChange={val => this.setState({ topSize: val })} getPopupContainer={trigger => trigger.parentNode} style={{ width: 135 }}>
              <Select.Option value={20}>{"Show Top 20"}</Select.Option>
              <Select.Option value={50}>{"Show Top 50"}</Select.Option>
              <Select.Option value={100}>{"Show Top 100"}</Select.Option>
            </Select>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24} xl={8}>
            <Card bodyStyle={{ textAlign: "center" }}>
              <h3>{`Top ${topSize} Art Units By Allowance Rate`}</h3>
              <TopTable title="" searchKey="examiner_art_unit" topSize={topSize} style={styles.table} filter={filter} />
            </Card>
          </Col>
          <Col span={24} xl={8}>
            <Card bodyStyle={{ textAlign: "center" }}>
              <h3>{`Top ${topSize} Examiners By Allowance Rate`}</h3>
              <TopTable title="" searchKey="examiner_name" topSize={topSize} style={styles.table} filter={filter} />
            </Card>
          </Col>
          <Col span={24} xl={8}>
            <Card bodyStyle={{ textAlign: "center" }}>
              <h3>
                {`Top ${topSize} Law Firms By `}
                <Dropdown overlay={LawFirmViewSelector}>
                  <Button>
                    {currentLawFirmView}
                    <DownOutlined />
                  </Button>
                </Dropdown>
              </h3>
              {currentLawFirmView === "LPIX" ? (
                <TopTableLpix title="Law Firm" topSize={topSize} style={styles.table} filter={filter} />
              ) : (
                <TopTable title="Law Firm" searchKey="law_firm" topSize={topSize} style={styles.table} filter={filter} />
              )}
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  _getFilter = () => {
    const query = keepOnlyProsecutionQueryStrings(this.props.location.query);
    return { ...omitBy({ ...defaultFilter, examiner_art_unit: query.art_unit }, isEmpty) };
  };
}

export default Prosecution;
